import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "@phosphor-icons/react";
import { adminVideoTagsQueryOptions } from "@/api-queries/videos";
import { inputBaseClassName } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface VideoTagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
}

/** Chips + free text. Enter or comma adds; suggestions come from existing tags. */
export function VideoTagInput({ value, onChange }: VideoTagInputProps) {
  const [draft, setDraft] = useState("");
  const { data } = useQuery(adminVideoTagsQueryOptions);

  const lower = new Set(value.map((v) => v.toLowerCase()));
  const suggestions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    return (data?.data ?? [])
      .filter((t) => !lower.has(t.name.toLowerCase()) && (!q || t.name.toLowerCase().includes(q)))
      .slice(0, 8);
  }, [data, draft, value]);

  const add = (name: string) => {
    const clean = name.trim().replace(/\s+/g, " ");
    if (!clean || lower.has(clean.toLowerCase()) || value.length >= 20) return;
    onChange([...value, clean]);
    setDraft("");
  };
  const remove = (name: string) => onChange(value.filter((v) => v !== name));

  return (
    <div className="space-y-2">
      <div className={cn(inputBaseClassName, "flex h-auto min-h-10 flex-wrap items-center gap-1.5 py-1.5")}>
        {value.map((tag) => (
          <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
            {tag}
            <button type="button" onClick={() => remove(tag)} aria-label={`Ukloni ${tag}`} className="rounded-full hover:bg-primary/20">
              <X className="size-3" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add(draft);
            } else if (e.key === "Backspace" && !draft && value.length) {
              remove(value[value.length - 1]!);
            }
          }}
          onBlur={() => draft.trim() && add(draft)}
          placeholder={value.length ? "" : "Dodaj tag i pritisni Enter"}
          className="min-w-[8rem] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => add(t.name)}
              className="rounded-full border border-border bg-background px-2.5 py-0.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              {t.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
